/**
 * Read a **provider** bundle locally, so an operator can see what is in it
 * before anything is written (REQ-UI-157; ADR-040 §D-3; Flow V §5c).
 *
 * Pure, React-free, and entirely client-side — see :file:`@/lib/bundle-archive`
 * for why, and for the gzip/tar/digest machinery this shares with the egress
 * twin. What lives here is the **provider** reading of the definition: the
 * archive carries ``provider.json`` rather than ``peer.json``, and its interfaces
 * carry a ``trust_mode`` the egress ones have no analogue for.
 *
 * # Trust mode is previewed, and that is not cosmetic
 *
 * An interface arriving as ``tunnel`` means the receiving deployment will admit
 * that provider's connections **without authenticating them itself** (ADR-023).
 * That is the single most consequential fact in a provider bundle, and an
 * operator reviewing an archive somebody handed them must see it *before*
 * installing rather than discovering it in the editor afterwards.
 *
 * Cross-references:
 *   - REQ-UI-157 (show what was parsed, block on unresolved placeholders).
 *   - REQ-PROVIDER-023 (the install route); ADR-040 §D-3 (seed-then-own).
 *   - `packages/meridian_bff/src/meridian_bff/_provider_routes.py` `_build_export_archive`
 *     (the layout this reads: ``MANIFEST.json`` + exactly one ``provider.json``).
 */
import {
  BundleUnreadable,
  asRecord,
  asString,
  collectTokens,
  gunzip,
  readJsonMember,
  readManifest,
  untar,
  type BundleArtifact,
  type BundlePlaceholder,
} from "@/lib/bundle-archive";

export { BundleUnreadable };
export type { BundleArtifact };

/** A placeholder the bundle ships with, and whether the operator has answered it. */
export type ProviderBundlePlaceholder = BundlePlaceholder;

/** One connection, as the bundle's provider definition describes it. */
export interface ProviderBundleConnection {
  name: string;
  role: string;
  endpointHost: string | null;
  endpointPort: number | null;
  /** ``true`` when any field on this connection still carries a ``${token}``. */
  hasUnresolved: boolean;
}

/** One interface, as the bundle's provider definition describes it. */
export interface ProviderBundleInterface {
  name: string;
  typeId: string;
  /**
   * How the receiving deployment will *attribute* connections on this interface.
   *
   * Previewed alongside the wire mode rather than left to the editor: ``tunnel``
   * says the network layer authenticates and Meridian does not, which is the
   * fact an operator most needs before agreeing to install (ADR-023).
   */
  trustMode: string | null;
  transportSecurity: string | null;
  conversionMode: string | null;
  connections: ProviderBundleConnection[];
  /** ``true`` when a field on the interface itself (not a connection) is unresolved. */
  hasUnresolved: boolean;
}

/** Everything the provider import dialog renders before it writes anything. */
export interface ProviderBundlePreview {
  bundleName: string;
  generatedBy: string;
  providerName: string;
  providerDescription: string | null;
  interfaces: ProviderBundleInterface[];
  artifacts: BundleArtifact[];
  placeholders: ProviderBundlePlaceholder[];
  /** Distinct ``${token}`` names still present anywhere in the documents. */
  unresolvedTokens: string[];
  /** Members whose bytes do not match the manifest, as operator-facing sentences. */
  digestProblems: string[];
}

function previewConnection(raw: unknown): ProviderBundleConnection {
  const record = asRecord(raw);
  return {
    name: asString(record.name) ?? "(unnamed)",
    // `listen` is the model's own default and the only realized ingress role, so
    // it is what a definition omitting the field means.
    role: asString(record.role) ?? "listen",
    endpointHost: asString(record.endpoint_host),
    endpointPort: typeof record.endpoint_port === "number" ? record.endpoint_port : null,
    hasUnresolved: collectTokens(record).length > 0,
  };
}

function previewInterface(raw: unknown): ProviderBundleInterface {
  const record = asRecord(raw);
  const connections = Array.isArray(record.connections)
    ? record.connections.map(previewConnection)
    : [];
  // Scoped to the interface's OWN fields: a token inside a connection is
  // reported on that connection's row, and counting it twice would tell an
  // operator there are more unanswered questions than the manifest asks.
  const { connections: _connections, ...own } = record;
  return {
    name: asString(record.name) ?? "(unnamed)",
    typeId: asString(record.type_id) ?? "(unknown)",
    trustMode: asString(record.trust_mode),
    transportSecurity: asString(record.transport_security),
    conversionMode: asString(record.conversion_mode),
    connections,
    hasUnresolved: collectTokens(own).length > 0,
  };
}

/**
 * Read a `.tar.gz` provider bundle into everything the import dialog shows.
 *
 * @param archive The bytes the operator selected.
 * @throws BundleUnreadable when the archive cannot be rendered honestly — not a
 *   gzip, no ``MANIFEST.json``, no ``provider.json``, unparseable JSON. Every
 *   message is written for an operator, because it is shown to them verbatim.
 */
export async function parseProviderBundle(archive: ArrayBuffer): Promise<ProviderBundlePreview> {
  const members = untar(await gunzip(archive));
  const manifest = asRecord(readJsonMember(members, "MANIFEST.json", "provider"));
  const definition = asRecord(readJsonMember(members, "provider.json", "provider"));
  const provider = asRecord(definition.provider);

  const unresolvedTokens = collectTokens(definition);
  const { bundleName, generatedBy, artifacts, placeholders, digestProblems } = await readManifest(
    manifest,
    members,
    unresolvedTokens,
  );

  return {
    bundleName,
    generatedBy,
    providerName: asString(provider.name) ?? "(unnamed provider)",
    providerDescription: asString(provider.description),
    interfaces: Array.isArray(provider.interfaces) ? provider.interfaces.map(previewInterface) : [],
    artifacts,
    placeholders,
    unresolvedTokens,
    digestProblems,
  };
}

/**
 * Whether this bundle may be installed from the dialog.
 *
 * **Unresolved placeholders block** (REQ-UI-157): installing with one persists
 * the literal ``${…}`` as configuration, and the interface then fails to
 * assemble far from the act that caused it, with nothing pointing back. A digest
 * problem blocks for a different reason — the archive is not the one the
 * manifest describes, and the BFF will refuse it anyway.
 *
 * This is the one control on the provider surface that is legitimately **visible
 * and disabled**: REQ-UI-143's absent-not-disabled rule governs *permissions*,
 * and the operator here has the permission and the surface has the gap.
 */
export function isProviderBundleInstallable(preview: ProviderBundlePreview): boolean {
  return preview.unresolvedTokens.length === 0 && preview.digestProblems.length === 0;
}
